import { Alert as TunaAlert, AlertTitle } from '../primitives/alert.js'
import { Button } from '../primitives/button.js'
import { Snackbar } from '../primitives/snackbar.js'
import { features, useSelector, useThunkDispatch } from '@tunasong/redux'
import type { FC } from 'react'
import { useCallback, useEffect, useState } from 'react'
import { getPathFromPathOrUrl } from '../navigation/get-path.js'
import { Link } from '../navigation/link.js'
import { useNavigate } from '../navigation/navigate.js'
import { graphHooks } from '@tunasong/graph-lib/react'
import styles from './alert.module.css'

export interface NotificationAlertProps {
  className?: string
  autoHideDuration?: number
}

export const NotificationAlert: FC<NotificationAlertProps> = props => {
  const { autoHideDuration = 10000 } = props
  const dispatch = useThunkDispatch()
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()

  const entityId = useSelector(state => state.presence.activeEntityId)
  const alert = useSelector(state => state.notifications.alert)
  const ancestors = graphHooks.useAncestors({ parentId: entityId ?? undefined })

  useEffect(() => {
    if (!alert) {
      return
    }
    setOpen(true)
  }, [alert, ancestors, entityId])

  const handleClose = useCallback(() => {
    setOpen(false)
    dispatch(features.notifications.actions.setAlert(null))
  }, [dispatch])

  const nav = useCallback(
    (href: string) => () => {
      void navigate(getPathFromPathOrUrl(href))
      setOpen(false)
    },
    [navigate]
  )

  if (!alert) {
    return null
  }

  const { severity, message, title, duration = autoHideDuration, actions, link } = alert

  const actionButtons =
    actions?.map(({ title, action }, idx) => (
      <Button key={idx} onClick={nav(action)}>
        {title}
      </Button>
    )) ?? []
  const action = <>{actionButtons}</>

  return (
    /**
     * The toast's one live region (GH #2407, Joni): assertive for an error, since the action just failed and the
     * user must know before moving on; polite otherwise, so a confirmation does not cut off what is being read.
     */
    <Snackbar
      open={open}
      autoHideDuration={duration}
      onClick={handleClose}
      onClose={handleClose}
      role={severity === 'error' ? 'alert' : 'status'}
      aria-live={severity === 'error' ? 'assertive' : 'polite'}
    >
      {/* The Snackbar is the one live region: an inner role="alert" announced the toast twice (GH #2407) */}
      <TunaAlert
        className={styles.toast}
        data-severity={severity ?? 'success'}
        severity={severity}
        action={action}
        role="none"
      >
        {title ? <AlertTitle>{title}</AlertTitle> : null}
        {link ? <Link to={link}>{message}</Link> : message}
      </TunaAlert>
    </Snackbar>
  )
}

export default NotificationAlert
